import React, { useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Longer than this and we clamp with a "Read more" rather than letting a wall of text push the
// services below the fold — especially on mobile, where the hero already costs a lot of height.
const CLAMP_AFTER_CHARS = 170;

/**
 * The org's public "about" text.
 *
 * Rendered BELOW the gradient hero rather than inside it: a 500-character paragraph on a coloured
 * background is hard to read, breaks the name -> type -> location hierarchy, and makes the banner
 * enormous on small screens. It sits in its own bordered card so it matches the surrounding
 * rhythm (alert, service cards) instead of floating as unanchored grey text.
 */
export default function OrgAboutBlurb({ text, sx }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;

  const isLong = text.length > CLAMP_AFTER_CHARS;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        ...sx,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' }}
        >
          About
        </Typography>
      </Stack>

      <Typography
        variant="body2"
        sx={{
          lineHeight: 1.65,
          color: 'text.primary',
          ...(expanded || !isLong ? {} : {
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
        }}
      >
        {text}
      </Typography>

      {isLong && (
        <Box sx={{ mt: 0.5 }}>
          <Button
            size="small"
            onClick={() => setExpanded((v) => !v)}
            sx={{ px: 0, minWidth: 0, textTransform: 'none', fontWeight: 700 }}
          >
            {expanded ? 'Show less' : 'Read more'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
